import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Grid, TextField, Typography } from '@material-ui/core';
import { OpenInNew } from '@material-ui/icons';
import { DatePicker } from '@material-ui/pickers';
import Button from '@material-ui/core/Button';
import { useTheme } from '@material-ui/core/styles';
import { formatDate } from '../../../util/date';

type StatisticsDateRangeSelectionProps = {
  defaultFrom: Date;
  defaultTo: Date;
  from: Date;
  to: Date;
  onChange: (from: Date, to: Date) => void;
};
export const StatisticsDateRangeSelection = ({
  defaultFrom,
  defaultTo,
  from,
  to,
  onChange,
}: StatisticsDateRangeSelectionProps): ReactElement => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [fromDate, setFromDate] = useState(from);
  const [toDate, setToDate] = useState(to);

  useEffect(() => {
    setFromDate(from);
  }, [from]);

  useEffect(() => {
    setToDate(to);
  }, [to]);

  const handleOk = () => {
    onChange(fromDate, toDate);
    handleClose();
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Typography
        variant="body2"
        color="secondary"
        component="div"
        style={{
          textDecoration: 'underline',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
        }}
        onClick={() => setIsOpen(true)}
      >
        {`${formatDate(fromDate)} - ${formatDate(toDate)}`}
        <OpenInNew style={{ height: '16px' }} />
      </Typography>
      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle>Zeitraum anpassen</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CustomDatePicker label={'Von'} value={fromDate} onChange={(date) => setFromDate(date)} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <CustomDatePicker label={'Bis'} value={toDate} onChange={(date) => setToDate(date)} fullWidth />
            </Grid>
            <Grid item xs={12} container justifyContent="flex-end">
              <SimpleLink
                onClick={() => {
                  setToDate(defaultTo);
                  setFromDate(defaultFrom);
                }}
                content="Zurücksetzen"
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" flexGrow={1} style={{ gap: theme.spacing(1) }} justifyContent="flex-end">
                <Button color="primary" variant={'outlined'} onClick={handleClose}>
                  Abbrechen
                </Button>
                <Button color="primary" variant={'contained'} onClick={handleOk}>
                  OK
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

type CustomDatePickerProps = {
  label: string;
  value: Date;
  onChange: (date: Date) => void;
  fullWidth?: boolean;
};

export const CustomDatePicker = ({ label, value, onChange, fullWidth }: CustomDatePickerProps): ReactElement => {
  return (
    <DatePicker
      label={label}
      value={value}
      onChange={onChange}
      inputFormat="dd.MM.yyyy"
      mask="__.__.____"
      renderInput={(props) => (
        <TextField
          {...props}
          size={'small'}
          label={label}
          placeholder="TT.MM.JJJJ"
          variant={'outlined'}
          fullWidth={fullWidth}
        />
      )}
    />
  );
};

type SimpleLinkProps = {
  onClick: () => void;
  content: string | ReactNode;
};

export const SimpleLink = ({ onClick, content }: SimpleLinkProps): ReactElement => {
  return (
    <Typography
      variant="body2"
      color="secondary"
      component="div"
      style={{
        textDecoration: 'underline',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
      }}
      onClick={onClick}
    >
      {content}
    </Typography>
  );
};
